import React, { useEffect, useRef, useState } from 'react';
import { ensureDesmos } from '../../lib/desmos';

interface Props {
  visible: boolean;
  onClose: () => void;
}

/**
 * Desmos GraphingCalculator 可拖动弹窗（SAT 官方所用类型）。
 *
 * 单实例常驻：计算器实例首次显示时创建后**不销毁**，仅通过 CSS 显隐切换，
 * 使用户在试卷与计算器间来回切换时保留已输入的表达式、且不重复创建实例。
 * 实例在组件卸载（离开做题页）时才 destroy 释放资源。
 */
export const DesmosCalculator: React.FC<Props> = ({ visible, onClose }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const calcRef = useRef<any>(null);
  const [error, setError] = useState<string | null>(null);

  // 拖拽位置（相对初始居中的偏移）
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const dragRef = useRef<{ startX: number; startY: number; baseX: number; baseY: number } | null>(null);

  // 首次可见时创建实例（懒加载），此后常驻
  useEffect(() => {
    if (!visible || calcRef.current) return;
    let cancelled = false;
    ensureDesmos()
      .then((Desmos) => {
        if (cancelled || !containerRef.current || calcRef.current) return;
        calcRef.current = Desmos.GraphingCalculator(containerRef.current, {
          expressions: true,
          settingsMenu: true,
          zoomButtons: true,
          language: navigator.language?.startsWith('zh') ? 'zh-CN' : 'en',
        });
      })
      .catch((e) => { if (!cancelled) setError(e?.message || 'Failed to load calculator'); });
    return () => { cancelled = true; };
  }, [visible]);

  // 组件卸载（离开做题页）时销毁实例
  useEffect(() => {
    return () => {
      if (calcRef.current) { calcRef.current.destroy(); calcRef.current = null; }
    };
  }, []);

  // 显隐切换后让 Desmos 重算尺寸（隐藏时容器无尺寸，恢复后需 resize）
  useEffect(() => {
    if (visible && calcRef.current?.resize) {
      // 下一帧容器已显示，再 resize
      requestAnimationFrame(() => calcRef.current?.resize?.());
    }
  }, [visible]);

  const onDragStart = (e: React.PointerEvent) => {
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      baseX: pos?.x ?? 0,
      baseY: pos?.y ?? 0,
    };
  };
  const onDragMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setPos({ x: dragRef.current.baseX + dx, y: dragRef.current.baseY + dy });
  };
  const onDragEnd = () => { dragRef.current = null; };

  const style: React.CSSProperties = pos
    ? { transform: `translate(calc(-50% + ${pos.x}px), calc(-50% + ${pos.y}px))` }
    : { transform: 'translate(-50%, -50%)' };

  return (
    <div
      className={`fixed left-1/2 top-1/2 z-50 ${visible ? '' : 'hidden'}`}
      style={style}
    >
      <div className="bg-white rounded-xl shadow-2xl border border-gray-200 w-[min(720px,94vw)] h-[min(600px,82vh)] flex flex-col">
        {/* 标题栏：拖拽手柄 */}
        <div
          className="h-10 shrink-0 flex items-center justify-between px-3 border-b border-gray-100 cursor-move select-none touch-none"
          onPointerDown={onDragStart}
          onPointerMove={onDragMove}
          onPointerUp={onDragEnd}
          onPointerCancel={onDragEnd}
        >
          <span className="text-sm font-medium text-gray-700">🖩 Calculator</span>
          <button
            onClick={onClose}
            onPointerDown={(e) => e.stopPropagation()}
            className="text-gray-400 hover:text-gray-600 text-sm cursor-pointer"
          >
            ✕
          </button>
        </div>
        {error ? (
          <div className="flex-1 flex items-center justify-center text-red-500 text-sm p-4">{error}</div>
        ) : (
          <div ref={containerRef} className="flex-1" />
        )}
      </div>
    </div>
  );
};
